import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Module-level singleton so every component shares one fetch per page view.
let cachedImages = null;
let pendingPromise = null;
const listeners = new Set();

async function fetchImages() {
  if (cachedImages) return cachedImages;
  if (pendingPromise) return pendingPromise;
  pendingPromise = api
    .get("/page-images")
    .then((r) => {
      cachedImages = r.data.images || {};
      pendingPromise = null;
      listeners.forEach((cb) => cb(cachedImages));
      return cachedImages;
    })
    .catch(() => {
      pendingPromise = null;
      return {};
    });
  return pendingPromise;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Returns a function `img(key, fallback)` that resolves a page-image key to a
 * fully-qualified URL. If the admin uploaded an image for the key, that one
 * wins; otherwise the fallback (the current hardcoded URL on the page) is
 * returned. Guarantees the site never looks broken even if the API fails.
 */
export function usePageImages() {
  const [images, setImages] = useState(cachedImages || {});
  useEffect(() => {
    let mounted = true;
    if (cachedImages) {
      setImages(cachedImages);
    } else {
      fetchImages().then((m) => mounted && setImages(m));
    }
    const cb = (m) => mounted && setImages(m);
    listeners.add(cb);
    return () => { mounted = false; listeners.delete(cb); };
  }, []);

  const resolve = (key, fallback = "") => {
    const url = images[key] || fallback;
    if (!url) return "";
    return url.startsWith("/api/") ? backendUrl + url : url;
  };
  return { img: resolve, images };
}
